import type { ContenidoPaquete, LecturaSinConexion, ResultadoLote, VentaSinConexion } from '../api/ops/pases';

/**
 * La copia de la puerta para validar **sin conexión** (6.2, G2; datos/pases-acceso.md §5.1), sin React:
 *
 * - `descifrar`: el contenido de `getDoorPackageContent` (AES-256-GCM con la clave del paquete, `data_key`, que da
 *   `createDoorPackage` una sola vez; el texto cifrado lleva la etiqueta de 16 bytes al final).
 * - `aplicar`: una copia entera (`full`) la sustituye; una de cambios (`delta`) pone encima los pases cambiados.
 * - `validarSinConexion`: la misma respuesta que daría `validatePass`, con la copia: firma Ed25519 del QR con las claves
 *   públicas de la copia (`v1|<negocio>|<código>|<kid>`), clave revocada, pase anulado o caducado, ya dentro (también lo
 *   leído en esta tablet y aún sin enviar), fuera de horario y `issued_after_package` (firma buena, pase que la copia no
 *   tiene: ámbar).
 * - `loteDe` y `tras`: lo leído sin conexión va en un lote (`syncDoorPackage`) y se quita lo que el servidor ya tiene.
 * - Taquilla sin conexión (10.7; G2, G4, G509): `articulosDeTaquilla` (lo que se vende sin conexión, con su precio, de
 *   `box_office.items`), `cupoSinConexion` (la parte de esta tablet menos lo vendido y aún sin enviar) y
 *   `elementosDeVenta`: la venta (`box_office_sale`) y, con «Entra ya», una llegada por entrada (`manual`,
 *   `local_sale_ref`) que Pases asocia a los pases que emite Eventos al recibir la venta.
 *
 * La copia nunca lleva `customer_id`, teléfono ni email (lo garantiza el servidor). La venta sí puede llevar el contacto que
 * escribió la taquilla («Al móvil» o «Por email», G970) para enviarle las entradas al volver la conexión.
 */

export interface PaseDeLaCopia {
    pass_id: string;
    code: string;
    status: 'active' | 'inside' | 'voided' | 'expired';
    passable_type?: string;
    valid_from: string;
    valid_to: string;
    issued_at?: string;
    holder_name?: string | null;
    door_display?: Record<string, unknown>;
    arrival?: { arrived_at: string; by?: { member_name?: string; device_name?: string } } | null;
}

export interface ClaveDeLaCopia {
    kid: string;
    public_key: string;
    status: string;
}

export interface Copia {
    business_id: string;
    door_package_id: string;
    access_group: { id: string; label: string; ref?: { type: string; id: string }; valid_from?: string; valid_to?: string };
    expires_at: string;
    passes: PaseDeLaCopia[];
    keys: ClaveDeLaCopia[];
    revoked_kids: string[];
    box_office: { quota: number; used: number; items?: unknown[] };
}

/** Lo que la tablet puede vender sin conexión (`DoorPreparation.offline_sale_items`, lo da Eventos al preparar la puerta). */
export interface ArticuloDeTaquilla {
    ref: { type: string; id: string };
    label: string;
    price: { amount: number; currency: string };
    nominative: boolean;
}

const desdeBase64 = (b64: string): Uint8Array<ArrayBuffer> => {
    const bin = atob(b64);
    const bytes = new Uint8Array(new ArrayBuffer(bin.length));
    for (let i = 0; i < bin.length; i += 1) {
        bytes[i] = bin.charCodeAt(i);
    }
    return bytes;
};
const desdeBase64Url = (b64u: string) => desdeBase64(b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64u.length % 4)) % 4));

export async function descifrar(contenido: Pick<ContenidoPaquete, 'ciphertext' | 'nonce'>, claveBase64: string): Promise<Copia> {
    const clave = await crypto.subtle.importKey('raw', desdeBase64(claveBase64), { name: 'AES-GCM' }, false, ['decrypt']);
    const claro = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: desdeBase64(contenido.nonce), tagLength: 128 }, clave, desdeBase64(contenido.ciphertext));
    return JSON.parse(new TextDecoder().decode(claro)) as Copia;
}

/** Una copia entera sustituye; una de cambios pone encima los pases que cambiaron (y las claves y el cupo de ahora). */
export function aplicar(anterior: Copia | null, nueva: Copia, tipo: 'full' | 'delta'): Copia {
    if (tipo === 'full' || anterior === null) {
        return nueva;
    }
    const pases = new Map(anterior.passes.map((p) => [p.pass_id, p]));
    for (const p of nueva.passes) {
        pases.set(p.pass_id, p);
    }
    return { ...nueva, passes: [...pases.values()] };
}

export type Color = 'green' | 'amber' | 'red';
export type Motivo = LecturaSinConexion['local_reason'];

export interface ResultadoLocal {
    result: Color;
    reason: Motivo;
    admissible: boolean;
    pass: PaseDeLaCopia | null;
    /** El código del QR o el tecleado (lo que va en el lote). */
    code: string | null;
}

/** Lo leído o vendido en esta tablet sin conexión, aún sin enviar. */
export interface Pendiente {
    item: LecturaSinConexion | VentaSinConexion;
    nombre: string | null;
}

/** Una lectura (lo guardado antes de la taquilla sin conexión no lleva `kind`: también es una lectura). */
export const esLectura = (item: Pendiente['item']): item is LecturaSinConexion => item.kind !== 'box_office_sale';
export const idDelElemento = (item: Pendiente['item']) => (esLectura(item) ? item.client_scan_id : item.client_item_id);

/** `v1.<negocio>.<código>.<kid>.<firma>` (la dirección entera o su última parte). */
export function leerQr(qr: string): { negocio: string; codigo: string; kid: string; firma: string } | null {
    const ultima = qr.trim().slice(qr.trim().lastIndexOf('/') + 1);
    const m = /^v1\.(biz_[0-9A-HJKMNP-TV-Z]{26})\.([0-9A-HJKMNP-TV-Z]{12})\.([a-z0-9]{4,12})\.([A-Za-z0-9_-]{86})$/.exec(ultima);
    return m === null ? null : { negocio: m[1] ?? '', codigo: m[2] ?? '', kid: m[3] ?? '', firma: m[4] ?? '' };
}

export async function firmaValida(clavePublica: string, mensaje: string, firma: string): Promise<boolean> {
    try {
        const clave = await crypto.subtle.importKey('raw', desdeBase64(clavePublica), { name: 'Ed25519' }, false, ['verify']);
        return await crypto.subtle.verify({ name: 'Ed25519' }, clave, desdeBase64Url(firma), new TextEncoder().encode(mensaje));
    } catch {
        return false;
    }
}

const color = (reason: Motivo): Color =>
    reason === 'ok' || reason === 'reentry' || reason === 'admitted_override'
        ? 'green'
        : reason === 'already_inside' || reason === 'concurrent_arrival' || reason === 'outside_window' || reason === 'list_cutoff' || reason === 'issued_after_package'
          ? 'amber'
          : 'red';

const resultado = (reason: Motivo, pass: PaseDeLaCopia | null, code: string | null): ResultadoLocal => ({
    result: color(reason),
    reason,
    admissible: reason === 'outside_window' || reason === 'list_cutoff' || reason === 'issued_after_package',
    pass,
    code,
});

/**
 * Valida con la copia, como `validatePass`. `entrada`: el QR leído o el código tecleado. `yaLeidos`: los códigos con una
 * llegada en esta tablet aún sin enviar.
 */
export async function validarSinConexion(copia: Copia, entrada: { qr: string } | { codigo: string }, ahora: Date, yaLeidos: ReadonlySet<string> = new Set()): Promise<ResultadoLocal> {
    let codigo: string;
    let firmaBuena = false;
    if ('qr' in entrada) {
        const qr = leerQr(entrada.qr);
        if (qr === null) {
            return resultado('bad_signature', null, null);
        }
        if (copia.revoked_kids.includes(qr.kid)) {
            return resultado('key_revoked', null, qr.codigo);
        }
        const clave = copia.keys.find((k) => k.kid === qr.kid);
        firmaBuena = clave !== undefined && (await firmaValida(clave.public_key, `v1|${qr.negocio}|${qr.codigo}|${qr.kid}`, qr.firma));
        if (!firmaBuena) {
            return resultado('bad_signature', null, qr.codigo);
        }
        if (qr.negocio !== copia.business_id) {
            return resultado('other_business', null, qr.codigo);
        }
        codigo = qr.codigo;
    } else {
        codigo = entrada.codigo.replace(/\s+/g, '').toUpperCase();
    }
    const pase = copia.passes.find((p) => p.code === codigo) ?? null;
    if (pase === null) {
        // Firma buena y la copia no lo tiene: se emitió después de la última actualización (ámbar, se puede admitir).
        return resultado(firmaBuena ? 'issued_after_package' : 'not_found', null, codigo);
    }
    if (pase.status === 'voided') {
        return resultado('voided', pase, codigo);
    }
    if (pase.status === 'expired') {
        return resultado('expired', pase, codigo);
    }
    if (pase.status === 'inside' || pase.arrival != null || yaLeidos.has(codigo)) {
        return resultado('already_inside', pase, codigo);
    }
    const t = ahora.getTime();
    if (t < Date.parse(pase.valid_from) || t > Date.parse(pase.valid_to)) {
        return resultado('outside_window', pase, codigo);
    }
    return resultado('ok', pase, codigo);
}

/** El elemento del lote para una lectura sin conexión. */
export function elementoDeLectura(r: ResultadoLocal, entrada: { qr: string } | { codigo: string }, miembro: string, cuando: Date, id: string = crypto.randomUUID(), decision: { decision: 'admitted' | 'rejected'; motivo?: string } | null = null): LecturaSinConexion {
    return {
        kind: 'scan',
        client_scan_id: id,
        method: 'qr' in entrada ? 'qr' : 'typed_code',
        ...('qr' in entrada ? { qr_payload: entrada.qr.slice(0, 300) } : {}),
        ...(r.code !== null && /^[0-9A-HJKMNP-TV-Z]{12}$/.test(r.code) ? { code: r.code } : {}),
        local_result: r.result,
        local_reason: r.reason,
        decision: decision?.decision ?? null,
        decision_reason: decision?.motivo ?? null,
        member_id: miembro,
        device_scanned_at: cuando.toISOString(),
    };
}

/** ¿Esta lectura deja a la persona dentro? (verde o ámbar admitido). */
export const dejaDentro = (item: Pendiente['item']) => esLectura(item) && (item.local_result === 'green' || item.decision === 'admitted');

export function loteDe(pendientes: readonly Pendiente[], ahora: Date, id: string = crypto.randomUUID()) {
    return { client_batch_id: id, device_sent_at: ahora.toISOString(), items: pendientes.map((p) => p.item) };
}

export interface ResumenEnvio {
    enviadas: number;
    conflictos: number;
    rechazadas: number;
    /** Ventas de taquilla que el servidor aceptó sin su contacto, que no valía (`contact_discarded`, G974). */
    sinContacto: number;
    borrar: boolean;
    /** Lo que sigue pendiente (lo que el servidor no ha contestado). */
    quedan: Pendiente[];
}

/** Tras `syncDoorPackage`: lo aceptado, duplicado, en conflicto o rechazado ya no está pendiente. */
export function tras(pendientes: readonly Pendiente[], r: ResultadoLote): ResumenEnvio {
    const contestados = new Map(r.items.map((i) => [i.client_item_id, i.outcome]));
    const cuenta = (o: string) => r.items.filter((i) => i.outcome === o).length;
    return {
        enviadas: cuenta('accepted') + cuenta('duplicate') + cuenta('forwarded'),
        conflictos: cuenta('conflict'),
        rechazadas: cuenta('rejected'),
        sinContacto: r.items.filter((i) => i.contact_discarded === true).length,
        borrar: r.wipe_required === true,
        quedan: pendientes.filter((p) => !contestados.has(idDelElemento(p.item))),
    };
}

/** Lo que queda del cupo de taquilla de esta tablet (G4, G509). */
export const cupoQueda = (b: { quota: number; used: number }) => Math.max(0, b.quota - b.used);

/** Las ventas de taquilla de esta tablet aún sin enviar y cuántas entradas llevan. */
export function ventasPorEnviar(pendientes: readonly Pendiente[]): { ventas: number; entradas: number } {
    const ventas = pendientes.map((p) => p.item).filter((i): i is VentaSinConexion => !esLectura(i));
    return { ventas: ventas.length, entradas: ventas.reduce((n, v) => n + v.lines.reduce((m, l) => m + l.quantity, 0), 0) };
}

/** Sin conexión, lo que queda de la parte de esta tablet: el cupo del paquete menos lo vendido y lo aún sin enviar (G509). */
export const cupoSinConexion = (b: { quota: number; used: number }, pendientes: readonly Pendiente[]) => Math.max(0, cupoQueda(b) - ventasPorEnviar(pendientes).entradas);

type ArticuloRecibido = Omit<ArticuloDeTaquilla, 'nominative'> & { nominative?: boolean };

const esArticulo = (v: unknown): v is ArticuloRecibido => {
    const a = v as Partial<ArticuloDeTaquilla> | null;
    return typeof a === 'object' && a !== null && typeof a.ref?.type === 'string' && typeof a.ref.id === 'string' && typeof a.label === 'string'
        && typeof a.price?.amount === 'number' && typeof a.price.currency === 'string';
};

export function articulosDeTaquilla(copia: Pick<Copia, 'box_office'>): ArticuloDeTaquilla[] {
    return (copia.box_office.items ?? []).filter(esArticulo).map((a) => ({ ...a, nominative: a.nominative ?? false }));
}

export interface VentaLocal {
    lineas: { ref: { type: string; id: string }; quantity: number; holder_names?: string[] }[];
    forma: 'cash' | 'card_terminal';
    total: { amount: number; currency: string };
    /** «Entra ya»: su llegada cuenta ya (una lectura por entrada asociada a la venta). */
    entraYa: boolean;
    /** «Al móvil» o «Por email» (G970): Pases lo valida y Eventos le envía las entradas al crear el pedido. */
    contacto?: VentaSinConexion['contact'];
}

/** La venta sin conexión y, con «Entra ya», una llegada por entrada con `local_sale_ref` (OfflineScanItem). */
export function elementosDeVenta(v: VentaLocal, miembro: string, cuando: Date, id: string = crypto.randomUUID()): Pendiente[] {
    const venta: VentaSinConexion = {
        kind: 'box_office_sale',
        client_item_id: id,
        member_id: miembro,
        device_sold_at: cuando.toISOString(),
        lines: v.lineas,
        payment_method: v.forma,
        total: v.total,
        ...(v.contacto === undefined ? {} : { contact: v.contacto }),
    };
    const elementos: Pendiente[] = [{ item: venta, nombre: null }];
    if (v.entraYa) {
        v.lineas.forEach((l, i) => {
            for (let n = 0; n < l.quantity; n += 1) {
                elementos.push({
                    item: { kind: 'scan', client_scan_id: `${id}-${String(i)}-${String(n)}`.slice(0, 64), method: 'manual', local_result: 'green', local_reason: 'ok',
                        member_id: miembro, device_scanned_at: cuando.toISOString(), local_sale_ref: id },
                    nombre: l.holder_names?.[n] ?? null,
                });
            }
        });
    }
    return elementos;
}
